import useInstagramTopPosts from './useInstagramTopPosts';
import styles from './InstagramTopPosts.module.css';
import { useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { SkeletonTableCell } from '@round/ui-kit';
import { buildInstagramAudioUrl, numberWithCommas } from 'helpers';
import moment from 'moment';
import TopPostsTable from '../Table/TopPostsTable';
import { instagram, OrderingValues } from '@round/api';
import { TruncatedLinkCell, LinkData } from '@round/whitelabel-ui';

export type InstagramTopPostsTableRow = instagram.InstagramPost & {
    audio: instagram.InstagramAudio | null;
};

type Props = {
    songId: number | null | undefined;
};

const InstagramTopPosts = ({ songId }: Props) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [ordering, setOrdering] = useState<OrderingValues<instagram.GetInstagramPostsSortableKeys>>('-view_count');

    const { data, audiosData, isLoading, isFetching, isError } = useInstagramTopPosts({
        songId,
        page,
        page_size: pageSize,
        ordering,
    });

    const rows: InstagramTopPostsTableRow[] =
        data?.results.map((post) => ({
            ...post,
            audio: post.audio_id ? audiosData[post.audio_id] ?? null : null,
        })) ?? [];

    const columns: ColumnDef<InstagramTopPostsTableRow, any>[] = [
        {
            header: 'Username',
            accessorFn: (row) => row.owner_username || '-',
            cell: SkeletonTableCell,
        },
        {
            header: 'Followers',
            accessorFn: (row) => (row.owner_follower_count ? numberWithCommas(row.owner_follower_count) : '-'),
            cell: SkeletonTableCell,
        },
        {
            header: 'Location',
            accessorFn: (row) => row.location || '-',
            cell: SkeletonTableCell,
        },
        {
            header: 'Description',
            accessorFn: (row): LinkData => ({
                href: row.post_url,
                value: row.text || '-',
            }),
            cell: TruncatedLinkCell,
        },
        {
            header: 'Audio',
            id: 'audio',
            accessorFn: (row): LinkData => {
                const url = row.audio?.instagram_id ? buildInstagramAudioUrl(row.audio.instagram_id) : undefined;

                const info = !row.audio
                    ? undefined
                    : `${row.audio.title || row.audio.instagram_id}${
                          row.audio.author_name ? ` - ${row.audio?.author_name}` : ''
                      }`;

                return {
                    href: url,
                    value: info || '-',
                };
            },
            cell: TruncatedLinkCell,
        },
        {
            header: 'Date Posted',
            accessorFn: (row) => (row.taken_at ? moment(row.taken_at).format('D MMM YY') : '-'),
            cell: SkeletonTableCell,
        },
        {
            header: 'Plays',
            enableSorting: true,
            id: 'play_count',
            accessorFn: (row) => (row.play_count ? numberWithCommas(row.play_count) : '-'),
            cell: SkeletonTableCell,
        },
        {
            header: 'Views',
            enableSorting: true,
            id: 'view_count',
            accessorFn: (row) => (row.view_count ? numberWithCommas(row.view_count) : '-'),
            cell: SkeletonTableCell,
        },
        {
            header: 'Likes',
            accessorFn: (row) => (row.like_count ? numberWithCommas(row.like_count) : '-'),
            cell: SkeletonTableCell,
        },
        {
            header: 'Comments',
            accessorFn: (row) => (row.comment_count ? numberWithCommas(row.comment_count) : '-'),
            cell: SkeletonTableCell,
        },
    ];

    return (
        <section className={styles.container}>
            <TopPostsTable
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={(pageSize) => {
                    setPageSize(pageSize);
                    setPage(1);
                }}
                ordering={ordering}
                onOrderingChange={(ordering) => setOrdering(ordering[0])}
                count={data?.count ?? 0}
                data={rows}
                columns={columns}
                meta={{ isLoading: isLoading || isFetching }}
                hasError={isError}
            />
        </section>
    );
};

export default InstagramTopPosts;
